import React, { useState, useMemo, useEffect, useCallback } from 'react'
import messageIcon from "../../img/messageIcon.svg"
import phoneIcon from "../../img/phoneIcon.svg"

/* ── field normalisation ── */
const displayStatus = (s) => {
  if (!s) return '—'
  const map = { in_progress: 'In Progress', resolved: 'Resolved', closed: 'Closed', on_hold: 'On Hold', re_opened: 'Re-Opened' }
  return map[s] || s
}
const displayPriority = (p) => {
  if (!p) return '—'
  return p.charAt(0).toUpperCase() + p.slice(1)
}
const displayChannel = (c) => {
  if (!c) return '—'
  const map = { call: 'Phone Call', whatsapp: 'WhatsApp', rider_app: 'Rider App', email: 'Email' }
  return map[c] || c
}
const displayStage = (s) => {
  if (!s) return '—'
  return s.toUpperCase()
}

const PRIORITY_OPTIONS   = ['All Priority', 'urgent', 'normal']
const STATUS_OPTIONS     = ['All Status', 'in_progress', 'on_hold', 'closed', 're_opened']
const CHANNEL_OPTIONS    = ['All Channels', 'call', 'whatsapp', 'rider_app', 'email']
const ESCALATION_OPTIONS = ['All Escalation', 'l1', 'l2', 'l3', 'l4']

const statusClass = (s) => {
  if (s === 'in_progress') return 'in-progress'
  if (s === 'resolved')    return 'resolved'
  if (s === 'on_hold')     return 'on-hold'
  if (s === 'closed')      return 'closed'
  if (s === 're_opened')   return 're-opened'
  return ''
}

const channelIcon = (ch) => {
  if (ch === 'call')      return <img src={phoneIcon} width={16} height={16} alt="phone" className="t360-channel-img" />
  if (ch === 'whatsapp')  return <img src={messageIcon} width={16} height={16} alt="message" />
  if (ch === 'rider_app') return '📱'
  if (ch === 'email')     return '✉️'
  return '🔗'
}

function FilterSelect({ value, onChange, options, labelMap }) {
  return (
    <div className="t360-select-wrap">
      <select className="t360-select" value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o} value={o}>{labelMap ? (labelMap[o] || o) : o}</option>)}
      </select>
      <span className="t360-select-arrow">▼</span>
    </div>
  )
}

const PRIORITY_LABELS   = { 'All Priority': 'All Priority', urgent: 'Urgent', normal: 'Normal' }
const STATUS_LABELS     = { 'All Status': 'All Status', in_progress: 'In Progress', on_hold: 'On Hold', closed: 'Closed', re_opened: 'Re-Opened' }
const CHANNEL_LABELS    = { 'All Channels': 'All Channels', call: 'Phone Call', whatsapp: 'WhatsApp', rider_app: 'Rider App', email: 'Email' }
const ESCALATION_LABELS = { 'All Escalation': 'All Escalation', l1: 'L1', l2: 'L2', l3: 'L3', l4: 'L4' }

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

function buildPageNumbers(current, total) {
  if (total <= 7) return Array.from({ length: total }, (_, i) => i + 1)
  const pages = [1]
  if (current > 3) pages.push('...')
  for (let i = Math.max(2, current - 1); i <= Math.min(total - 1, current + 1); i++) pages.push(i)
  if (current < total - 2) pages.push('...')
  pages.push(total)
  return pages
}

export default function Ticket360Page({ setCurrentPage, setSelectedTicket, fetchJson }) {
  const [tickets, setTickets]         = useState([])
  const [loading, setLoading]         = useState(false)
  const [totalCount, setTotalCount]   = useState(0)
  const [page, setPage]               = useState(1)
  const [pageSize, setPageSize]       = useState(10)
  const [search,     setSearch]       = useState('')
  const [priority,   setPriority]     = useState('All Priority')
  const [status,     setStatus]       = useState('All Status')
  const [channel,    setChannel]      = useState('All Channels')
  const [escalation, setEscalation]   = useState('All Escalation')

  // Reset to page 1 whenever filters, search, or page size change
  useEffect(() => { setPage(1) }, [search, priority, status, channel, escalation, pageSize])

  const fetchTickets = useCallback(() => {
    if (!fetchJson) return
    const params = new URLSearchParams()
    if (search.trim())                    params.set('q', search.trim())
    if (priority !== 'All Priority')      params.set('priority', priority)
    if (status !== 'All Status')          params.set('status', status)
    if (channel !== 'All Channels')       params.set('channel', channel)
    if (escalation !== 'All Escalation')  params.set('escalated_to', escalation)
    params.set('page', String(page))
    params.set('page_size', String(pageSize))

    setLoading(true)
    fetchJson(`/api/tickets/ticket/?${params.toString()}`)
      .then(data => {
        const rows = Array.isArray(data) ? data : (data?.results || [])
        setTickets(rows)
        setTotalCount(Array.isArray(data) ? rows.length : (data?.count ?? rows.length))
      })
      .catch(() => setTickets([]))
      .finally(() => setLoading(false))
  }, [fetchJson, search, priority, status, channel, escalation, page, pageSize])

  useEffect(() => {
    const t = setTimeout(fetchTickets, search ? 300 : 0)
    return () => clearTimeout(t)
  }, [fetchTickets])

  const totalPages = Math.max(1, Math.ceil(totalCount / pageSize))

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket)
    setCurrentPage('ticket-detail')
  }

  const handleClear = () => {
    setSearch('')
    setPriority('All Priority')
    setStatus('All Status')
    setChannel('All Channels')
    setEscalation('All Escalation')
    setPage(1)
  }

  return (
    <div className="t360-page">
      <p className="t360-subheading">
        Complete ticket intelligence — all attributes, evidence, lifecycle &amp; SLA signals in one view
      </p>

      <div className="t360-filters">
        <input
          className="t360-search"
          placeholder="Search by Ticket ID, VIN, Mobile, Customer..."
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <FilterSelect value={priority}   onChange={setPriority}   options={PRIORITY_OPTIONS}   labelMap={PRIORITY_LABELS} />
        <FilterSelect value={status}     onChange={setStatus}     options={STATUS_OPTIONS}     labelMap={STATUS_LABELS} />
        <FilterSelect value={channel}    onChange={setChannel}    options={CHANNEL_OPTIONS}    labelMap={CHANNEL_LABELS} />
        <FilterSelect value={escalation} onChange={setEscalation} options={ESCALATION_OPTIONS} labelMap={ESCALATION_LABELS} />
        <button className="t360-clear-btn" onClick={handleClear}>Clear</button>
        <span className="t360-ticket-count">
          {loading ? 'Loading…' : `${totalCount} ticket${totalCount !== 1 ? 's' : ''}`}
        </span>
      </div>

      <div className="t360-table-wrap" style={{ position: 'relative' }}>
        {loading && tickets.length > 0 && (
          <div style={{
            position: 'absolute', inset: 0, zIndex: 10,
            background: 'rgba(248,250,252,0.7)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            borderRadius: 8,
          }}>
            <div style={{
              display: 'flex', alignItems: 'center', gap: 10,
              background: '#fff', border: '1px solid #E2E8F0',
              borderRadius: 10, padding: '10px 20px',
              boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
              fontSize: 13, fontWeight: 500, color: '#374151',
            }}>
              <div style={{
                width: 16, height: 16, border: '2px solid #E2E8F0',
                borderTop: '2px solid #0090D9', borderRadius: '50%',
                animation: 'spin 0.7s linear infinite', flexShrink: 0,
              }} />
              Loading tickets…
            </div>
          </div>
        )}
        <table className="t360-table">
          <thead>
            <tr>
              <th>Ticket</th>
              <th>Type</th>
              <th>Customer</th>
              <th>Vehicle VIN</th>
              <th>Category / Sub</th>
              <th>Pri</th>
              <th>Status</th>
              <th>Assigned</th>
              <th>Created</th>
              <th>Channel</th>
              <th>Stage</th>
              <th>Details</th>
            </tr>
          </thead>
          <tbody>
            {loading && tickets.length === 0 ? (
              <tr>
                <td colSpan={12} className="t360-empty">Loading tickets…</td>
              </tr>
            ) : tickets.length === 0 ? (
              <tr>
                <td colSpan={12} className="t360-empty">No tickets match your filters.</td>
              </tr>
            ) : (
              tickets.map(t => (
                <tr key={t.ticket_id || t.ticket_number} onClick={() => handleViewTicket(t)} style={{ cursor: 'pointer' }}>
                  <td>
                    <span className="t360-ticket-id">{t.ticket_number}</span>
                  </td>

                  <td>
                    <span style={{ fontSize: 12, color: '#374151' }}>
                      {t.ticket_type ? t.ticket_type.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()) : '—'}
                    </span>
                  </td>

                  <td>
                    <span className="t360-customer-name">{t.customer_name || '—'}</span>
                    <span className="t360-customer-id">{t.customer_code || t.customer_phone || ''}</span>
                  </td>

                  <td>
                    <span className="t360-vin-main">{t.vin_number || '—'}</span>
                    <span className="t360-vin-plate">{t.vehicle_registration_number || ''}</span>
                  </td>

                  <td>
                    <span className="t360-cat-main">{t.issue_category || '—'}</span>
                    <span className="t360-cat-sub">{t.issue_sub_category || ''}</span>
                  </td>

                  <td>
                    <span className={`t360-priority ${t.priority || ''}`}>
                      {displayPriority(t.priority)}
                    </span>
                  </td>

                  <td>
                    <span className={`t360-status ${statusClass(t.status)}`}>
                      <span className="t360-status-dot" />
                      {displayStatus(t.status)}
                    </span>
                  </td>

                  <td>
                    <span className="t360-assigned">{t.assigned_to || '—'}</span>
                  </td>

                  <td>
                    <span className="t360-created">{t.created_time || t.created_date || '—'}</span>
                  </td>

                  <td>
                    <span className="t360-channel">
                      <span className="t360-channel-icon">{channelIcon(t.channel)}</span>
                      <span>{displayChannel(t.channel)}</span>
                    </span>
                  </td>

                  <td>
                    <span className={`t360-esc ${(t.ticket_stage || '').toLowerCase()}`}>
                      {displayStage(t.ticket_stage)}
                    </span>
                  </td>

                  <td>
                    <button className="t360-view-btn" onClick={e => { e.stopPropagation(); handleViewTicket(t) }}>View</button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="t360-pagination">
        {totalPages > 1 && (
          <>
            <button
              className="t360-page-btn"
              onClick={() => setPage(p => p - 1)}
              disabled={page === 1 || loading}
            >
              ← Prev
            </button>

            {buildPageNumbers(page, totalPages).map((p, i) =>
              p === '...' ? (
                <span key={`ellipsis-${i}`} className="t360-page-ellipsis">…</span>
              ) : (
                <button
                  key={p}
                  className={`t360-page-btn${p === page ? ' active' : ''}`}
                  onClick={() => setPage(p)}
                  disabled={loading}
                >
                  {p}
                </button>
              )
            )}

            <button
              className="t360-page-btn"
              onClick={() => setPage(p => p + 1)}
              disabled={page === totalPages || loading}
            >
              Next →
            </button>

            <span className="t360-page-info">
              Page {page} of {totalPages}
            </span>
          </>
        )}

        {/* Page size selector — always visible */}
        <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 6 }}>
          <span style={{ fontSize: 12, color: '#6B7280', whiteSpace: 'nowrap' }}>Rows per page:</span>
          <div className="t360-select-wrap" style={{ minWidth: 70 }}>
            <select
              className="t360-select"
              value={pageSize}
              onChange={e => setPageSize(Number(e.target.value))}
              disabled={loading}
            >
              {PAGE_SIZE_OPTIONS.map(n => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
            <span className="t360-select-arrow">▼</span>
          </div>
        </div>
      </div>
    </div>
  )
}
